<script>
import BlockingContainer from '@components/BlockingContainer';
import DownloadModel from '@models/Download';
import DownloadCategoryModel from '@models/DownloadCategory';

export default {
  components: {
    BlockingContainer,
  },
  props: {
    // eslint-disable-next-line
    loadedId: {
      default: null,
    },
    initialCategory: {
      default: null,
      type: Object,
    },
  },
  data() {
    return {
      categories: [],
      download: {
        name: null,
        download_category_id: null,
        remark: null,
        file: null,
      },
      selectedCategoryObject: null,
      initialLocationValue: null,
      fileLabel: null,
      removeFile: false,
    };
  },
  computed: {
    showRemark() {
      return this.selectedCategoryObject && this.selectedCategoryObject.show_remark;
    },
  },
  mounted() {
    this.loadCategories().then(() => {
      if (this.loadedId) {
        this.$wait.start('download processing');
        DownloadModel.find(this.loadedId)
          .then((response) => {
            this.download = {
              id: response.id,
              name: response.name,
              download_category_id: response.download_category_id,
              remark: response.remark,
              file: null,
            };
            this.fileLabel = response.file_name;
            if (response.location) {
              this.initialLocationValue = response.location;
            }
            this.selectedCategoryObject = this.categories.find((c) => c.id === response.download_category_id) || null;
            this.$wait.end('download processing');
          })
          .catch(() => this.$wait.end('download processing'));
      } else if (this.initialCategory) {
        this.download.download_category_id = this.initialCategory.id;
        this.selectedCategoryObject = this.initialCategory;
      }
    });
  },
  methods: {
    loadCategories() {
      return DownloadCategoryModel.orderBy('name')
        .get()
        .then((response) => {
          this.categories = response.data || response;
        });
    },
    onCategoryChange(id) {
      this.download.download_category_id = id;
      this.selectedCategoryObject = this.categories.find((c) => c.id === id) || null;
      // Reset category-specific fields
      if (!this.showGoogleLink) {
        this.initialLocationValue = null;
      }
      if (!this.showRemark) {
        this.download.remark = null;
      }
    },
    onFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.fileLabel = file.name;
        this.removeFile = false;
      }
    },
    clearFile() {
      this.download.file = null;
      this.fileLabel = null;
      this.removeFile = true;
    },
    validate() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.$wait.start('download processing');
          this.submit();
        }
      });
    },
    submit() {
      const data = Object.assign({}, this.download);
      if (this.loadedId && !data.file) {
        delete data.file;
      }
      if (this.removeFile) {
        data.remove_file = 1;
      }
      new DownloadModel(data)
        .save()
        .then(() => {
          this.$wait.end('download processing');
          this.$emit('submitted');
        })
        .catch(() => this.$wait.end('download processing'));
    },
  },
};
</script>

<template>
  <div>
    <BlockingContainer v-if="$wait.is('download processing')" />
    <b-form @submit.prevent="validate">

      <!-- Category -->
      <b-form-group :label="`${$t('fields.category')}:`">
        <b-form-select
          v-model="download.download_category_id"
          v-validate="'required'"
          data-vv-name="download_category_id"
          :data-vv-as="$t('fields.category')"
          :options="categories.map((c) => ({ value: c.id, text: c.name }))"
          :state="errors.has('download_category_id') ? false : null"
          @change="onCategoryChange"
        >
          <template #first>
            <b-form-select-option :value="null" disabled>{{ $t('placeholders.select_category') }}</b-form-select-option>
          </template>
        </b-form-select>
        <b-form-invalid-feedback>{{ errors.first('download_category_id') }}</b-form-invalid-feedback>
      </b-form-group>

      <!-- Name -->
      <b-form-group :label="`${$t('fields.name')}:`">
        <b-form-input
          v-model="download.name"
          v-validate="'required|max:255'"
          data-vv-name="name"
          :data-vv-as="$t('fields.name')"
          type="text"
          :state="errors.has('name') ? false : null"
          :placeholder="$t('placeholders.name')"
        />
        <b-form-invalid-feedback>{{ errors.first('name') }}</b-form-invalid-feedback>
      </b-form-group>

      <!-- File attachment -->
      <b-form-group :label="`${$t('fields.attachment')}:`">
        <b-form-file
          v-model="download.file"
          v-validate="loadedId ? '' : 'required'"
          data-vv-name="file"
          :data-vv-as="$t('fields.attachment')"
          accept=".pdf,.jpg,.jpeg,.png,.mp4"
          :placeholder="fileLabel || $t('placeholders.select_file_with_mp4')"
          :state="errors.has('file') ? false : null"
          @change="onFileChange"
        />
        <b-form-text v-if="fileLabel && !download.file" class="d-flex align-items-center">
          {{ $t('infos.current_file') }}: {{ fileLabel }}
          <b-link class="ml-2 text-danger" @click="clearFile">&times;</b-link>
        </b-form-text>
        <b-form-invalid-feedback>{{ errors.first('file') }}</b-form-invalid-feedback>
      </b-form-group>

      <!-- Remark (Anfahrten/Locations & Sonstiges) -->
      <b-form-group v-if="showRemark" :label="`${$t('fields.remark')}:`">
        <b-form-input
          v-model="download.remark"
          type="text"
          :placeholder="$t('placeholders.remark')"
        />
      </b-form-group>

      <b-button
        :disabled="!!errors.items.length"
        type="submit"
        :variant="'outline-' + (!errors.items.length ? 'dark' : 'danger')"
      >
        {{ $t('actions.submit') }}
      </b-button>
    </b-form>
  </div>
</template>
